import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import { ConditionalWrapper } from 'components/conditional-wrapper';
import FullPageScreen from 'components/full-page-screen';
import PaymentMethod from 'components/payment-method';
import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { getCurrentSelectedMethodType } from 'components/payment-method/utils';
import { ThreeDSChallengeModal } from 'components/three-ds-challenge-modal';
import { CartPaymentCardType, PaymentMethodBrand } from 'generated/rbi-graphql';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { useCartEntriesDayParts } from 'state/order/hooks/use-cart-entries-day-parts';
import { useOrderTimedFire } from 'state/order/hooks/use-order-timed-fire';
import { usePaymentContext } from 'state/payment';
import { CASH_ACCOUNT_IDENTIFIER } from 'state/payment/constants';
import { ThreeDSType } from 'state/payment/hooks/types';
import { useServiceModeContext } from 'state/service-mode';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import logger from 'utils/logger';
import { routes } from 'utils/routing';
import { waitForElm } from 'utils/wait-for-element';

import { PlaceOrderButton } from '../../../place-order-button';
import SectionHeading from '../../../section-heading';
import { AddNewCreditCardWrapper } from '../../add-new-credit-card-wrapper';
import DeliveryCancellationPolicy from '../../delivery-cancellation-policy';
import PaymentSelection from '../../payment-selection';
import { PaymentInfoBackground, PaymentSection } from '../../styled';
import { OrderPaymentProps } from '../../types';
import Totals from '../Totals';
import { useOrderPayment } from '../use-order-payment';

import { PaycometCreditCardHostedPaymentForm } from './paycomet-credit-card-form/paycomet-credit-card-form';
import { Card, OutcomeResult } from './types';

const StyledFullPageScreen = styled(FullPageScreen)`
  background: ${Styles.color.background};

  h1 {
    text-transform: capitalize;
  }
`;

export const PaycometHostedPagePayment: React.FC<OrderPaymentProps> = ({
  serverOrder,
  cartEntries,
  onPriceOrder,
  pricedAndAvailable,
}) => {
  const { formatMessage } = useIntl();
  const [tpvToken, setTpvToken] = useState('');

  const [isCheckoutErrorsModalOpenPaycomet, setIsCheckoutErrorsModalOpenPaycomet] = useState(false);
  const [isPaymentError, setIsPaymentError] = useState(false);
  const [isAlreadyPricedOrder, setIsAlreadyPricedOrder] = useState(false);
  const [isProcessingPaymentPaycomet, setIsProcessingPaymentPaycomet] = useState(false);
  const { cartEntriesActiveDayParts } = useCartEntriesDayParts(cartEntries);
  const order = useOrderContext();
  const { serviceMode, isDelivery, isCurbside, isTableService } = useServiceModeContext();
  const { checkoutPaymentMethodId, setCheckoutPaymentMethodId } = usePaymentContext();
  const { enableOrderTimedFire } = useOrderTimedFire({
    serverOrder,
    cartEntriesActiveDayParts,
  });
  const [isAvoidingCashDefaultPaymentAlready, setIsAvoidingCashDefaultPaymentAlready] =
    useState(false);
  const enableSortPaymentMethods = useFlag(LaunchDarklyFlag.ENABLE_SORT_PAYMENT_METHODS);

  const {
    enableDelivery,
    ErrorDialog,
    handlePaymentMethodSelected,
    handlePaycometSuccess,
    isPlacingOrder,
    isValidTip,
    offerIsValid,
    paymentMethods,
    placeOrderAriaLabel,
    setCommitOrderAfterPricing,
    setIsHandlingOrderCommit,
    setShowAddPaymentMethod,
    shouldConfirmStoreOnCheckout,
    showAddPaymentMethod,
    showPaymentMethodSelection,
    onChange,
    paymentValues,
    errors,
    setIsPaycometPaypalMethodSelected,
    isPaycometPaypalMethodSelected,
    setIsPaycometSodexoMethodSelected,
    isPaycometSodexoMethodSelected,
    setIsPaycometChequeGourmetMethodSelected,
    isPaycometChequeGourmetMethodSelected,
    threeDS: paymentThreeDS,
    setThreeDSChallengeTokenResponse,
    threeDSChallengeTokenResponse,
  } = useOrderPayment({
    enableOrderTimedFire,
    onPriceOrder,
    serverOrder,
  });

  useEffect(() => {
    const isChallengeRequestComplete = threeDSChallengeTokenResponse;

    if (isChallengeRequestComplete) {
      setIsHandlingOrderCommit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threeDSChallengeTokenResponse]);

  const { getErrorMessages } = useGetPaymentErrors();

  const paymentErrorMessages = getErrorMessages({
    invalidOfferError: !offerIsValid,
    invalidPaymentMethodError: isPaymentError,
    invalidTipAmountError: !isValidTip,
    pricedAndAvailableError: !pricedAndAvailable,
  });

  const selectedMethod = paymentMethods.find(
    method => method.accountIdentifier === checkoutPaymentMethodId
  );

  const currentSelectedMethodType = getCurrentSelectedMethodType({
    isAddGiftCardMethodSelected: false,
    isAddNewCardMethodSelected: showAddPaymentMethod,
    selectedMethodExist: !!selectedMethod,
  });

  const redirectCheckout = (): void => {
    window.location.href = '/cart';
  };

  const paymentThreeDSChallengeFlow = useMemo(
    () => ({
      activeType: paymentThreeDS.activeFlow,
      acsUrl: paymentThreeDS.acsUrl,
      challengeRequest: paymentThreeDS.challengeRequest,
    }),
    [paymentThreeDS.activeFlow, paymentThreeDS.acsUrl, paymentThreeDS.challengeRequest]
  );

  const sendPaycometToken = useCallback(async () => {
    // remove tpvToken case exist, because need get new tpvtoken after submit
    const tpvInput = document.querySelector('input[name="paytpvToken"]');
    tpvInput?.parentNode?.removeChild(tpvInput);
    // get new tpvToken
    const { defaultValue } = (await waitForElm('input[name="paytpvToken"]')) as HTMLInputElement;
    setTpvToken(defaultValue);
  }, []);

  const handleOnSubmit = useCallback(() => {
    if (selectedMethod && selectedMethod.accountIdentifier) {
      //If any error is present at last step we abort payment and show error modal
      if (paymentErrorMessages?.length) {
        setIsCheckoutErrorsModalOpenPaycomet(true);
        return;
      }

      setIsProcessingPaymentPaycomet(true);

      if (selectedMethod.accountIdentifier === CASH_ACCOUNT_IDENTIFIER) {
        shouldConfirmStoreOnCheckout(true);
        if (isCurbside && onPriceOrder) {
          setCommitOrderAfterPricing(true);
          return onPriceOrder({
            customerName: `CS-${LocalStorage.getItem(StorageKeys.VEHICLE_DESCRIPTION)}`,
          });
        }
        if (isTableService && onPriceOrder) {
          setCommitOrderAfterPricing(true);
          return onPriceOrder({
            customerName: `TABLE-${LocalStorage.getItem(StorageKeys.TABLE_NUMBER)}`,
          });
        }
        setIsHandlingOrderCommit(true);
        setIsProcessingPaymentPaycomet(false);
        return;
      }
    }

    setIsProcessingPaymentPaycomet(false);
    if (currentSelectedMethodType === CurrentSelectedMethodType.ADD_NEW_CARD) {
      sendPaycometToken();
    } else {
      setIsHandlingOrderCommit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedMethodType, selectedMethod, sendPaycometToken]);

  const onPaymentOutcome = useCallback(
    (outcome: OutcomeResult) => {
      setIsProcessingPaymentPaycomet(false);

      if (outcome.type === 'Failed') {
        logger.error({
          message: 'Paycomet Payment: Hosted page integration failed',
          outcome,
        });

        setIsPaymentError(true);
        setIsCheckoutErrorsModalOpenPaycomet(true);
        if (showAddPaymentMethod) {
          setShowAddPaymentMethod(false);
          setTimeout(() => setShowAddPaymentMethod(true), 1000);
        }
        return;
      }
      const { merchantAccount, paymentMethod, pspReference, billingZip } = outcome;

      const basePayload = {
        merchantAccount,
        pspReference,
        isCardNumberValid: true,
        isExpiryValid: true,
        billingStreetAddress: null,
        billingApt: null,
        billingCity: null,
        billingState: null,
        billingZip: '',
        billingCountry: null,
        billingAddressSameAsDelivery: false,
      };

      if (paymentMethod.source === CartPaymentCardType.PAYPAL) {
        const paypalPayload = {
          ...basePayload,
          nameOnCard: paymentMethod.email,
          cardNumber: '',
          isPaypal: true,
          cardType: CartPaymentCardType.PAYPAL,
          cvv: '',
          expiry: '',
          saveCard: false,
        };
        handlePaycometSuccess(paypalPayload);
      }

      if (paymentMethod.source === CartPaymentCardType.CREDIT) {
        const myCard = paymentMethod as Card;
        const cardNumberValue =
          myCard.storePaymentMethod && myCard.storedPaymentMethodId
            ? myCard.storedPaymentMethodId
            : '';
        const cardPayload = {
          ...basePayload,
          cardNumber: cardNumberValue,
          nameOnCard: paymentMethod.cardholderName || '',
          card: {
            bin: paymentMethod.bin,
            last4: paymentMethod.last4,
            expiryMonth: paymentMethod.expiryMonth,
            expiryYear: paymentMethod.expiryYear,
          },
          cardType: paymentMethod.brand,
          cvv: '',
          saveCard: paymentMethod.storePaymentMethod || false,
          expiry: `${paymentMethod.expiryMonth}${paymentMethod.expiryYear}`,
          billingZip: billingZip || '',
        };
        let paymentMethodBrand: PaymentMethodBrand | undefined;

        if (isPaycometSodexoMethodSelected || isPaycometChequeGourmetMethodSelected) {
          paymentMethodBrand = isPaycometSodexoMethodSelected
            ? PaymentMethodBrand.SODEXO
            : PaymentMethodBrand.CHEQUE_GOURMET;
        }

        handlePaycometSuccess({ ...cardPayload, paymentMethodBrand });
      }

      setIsHandlingOrderCommit(true);
    },
    [
      setIsHandlingOrderCommit,
      showAddPaymentMethod,
      setShowAddPaymentMethod,
      handlePaycometSuccess,
      isPaycometSodexoMethodSelected,
      isPaycometChequeGourmetMethodSelected,
    ]
  );

  if (!serverOrder) {
    return null;
  }

  if (isTableService && onPriceOrder && !isAlreadyPricedOrder) {
    setIsAlreadyPricedOrder(true);
    onPriceOrder({
      customerName: `TABLE-${LocalStorage.getItem(StorageKeys.TABLE_NUMBER)}`,
    });
  }

  const avoidCashDefault =
    enableSortPaymentMethods &&
    checkoutPaymentMethodId === CASH_ACCOUNT_IDENTIFIER &&
    !isAvoidingCashDefaultPaymentAlready;

  if (avoidCashDefault) {
    setIsAvoidingCashDefaultPaymentAlready(true);
    setCheckoutPaymentMethodId('');
  }

  const showPaymentSodexo = isPaycometSodexoMethodSelected !== 'voucher';
  const showPaymentChequeGourmet = isPaycometChequeGourmetMethodSelected !== 'voucher';
  return (
    <StyledFullPageScreen heading={formatMessage({ id: 'orderPayment' })} closeHref={routes.cart}>
      {isCheckoutErrorsModalOpenPaycomet && (
        <CheckoutErrorsModal
          checkoutErrors={paymentErrorMessages}
          closeModal={() => {
            redirectCheckout();
            setIsCheckoutErrorsModalOpenPaycomet(false);
          }}
        />
      )}
      <PaymentSection data-testid="cart-hosted-page-payment">
        <SectionHeading id="payment-method-header">
          {formatMessage({ id: 'paymentDetails' })}
        </SectionHeading>
        <PaymentSelection>
          <Totals
            serverOrder={serverOrder}
            order={order}
            serviceMode={serviceMode}
            isDisabled={isProcessingPaymentPaycomet || isPlacingOrder}
          />
          {isDelivery && enableDelivery && <DeliveryCancellationPolicy />}
          <PaymentInfoBackground $hostedPayment>
            {showPaymentMethodSelection && (
              <PaymentMethod
                serverOrder={serverOrder}
                aria-expanded="true"
                aria-labelledby="payment-method-header"
                hasBorder={false}
                isAddCardOpen={showAddPaymentMethod}
                methods={paymentMethods}
                openAddCardOnClick={setShowAddPaymentMethod}
                handleSelect={handlePaymentMethodSelected}
                selectedMethodId={checkoutPaymentMethodId}
                openPaycometPaypalOnClick={setIsPaycometPaypalMethodSelected}
                isPaycometPaypalMethodSelected={isPaycometPaypalMethodSelected}
                setIsPaycometSodexoMethodSelected={setIsPaycometSodexoMethodSelected}
                isPaycometSodexoMethodSelected={isPaycometSodexoMethodSelected}
                setIsPaycometChequeGourmetMethodSelected={setIsPaycometChequeGourmetMethodSelected}
                isPaycometChequeGourmetMethodSelected={isPaycometChequeGourmetMethodSelected}
                onResult={onPaymentOutcome}
              />
            )}
            {showAddPaymentMethod && showPaymentSodexo && showPaymentChequeGourmet && (
              <ConditionalWrapper
                condition={!paymentMethods.length}
                wrapper={children => <AddNewCreditCardWrapper>{children}</AddNewCreditCardWrapper>}
              >
                <PaycometCreditCardHostedPaymentForm
                  rbiOrder={serverOrder}
                  onResult={onPaymentOutcome}
                  onChange={onChange}
                  paymentValues={paymentValues}
                  errors={errors}
                  tpvToken={tpvToken}
                  onError={() => {
                    setShowAddPaymentMethod(false);
                    setIsPaymentError(false);
                    setIsCheckoutErrorsModalOpenPaycomet(true);
                  }}
                />
              </ConditionalWrapper>
            )}
          </PaymentInfoBackground>
          {!isProcessingPaymentPaycomet &&
            paymentThreeDSChallengeFlow.activeType === ThreeDSType.CHALLENGE &&
            paymentThreeDSChallengeFlow.acsUrl &&
            paymentThreeDSChallengeFlow.challengeRequest && (
              <ThreeDSChallengeModal
                acsUrl={paymentThreeDSChallengeFlow.acsUrl}
                challengeRequestToken={paymentThreeDSChallengeFlow.challengeRequest}
                onThreeDSAbort={() => redirectCheckout()}
                onChallengeSuccess={e => {
                  if (!e) {
                    setShowAddPaymentMethod(false);
                    setIsPaymentError(true);
                    setIsCheckoutErrorsModalOpenPaycomet(true);
                  } else {
                    setIsProcessingPaymentPaycomet(true);
                    setThreeDSChallengeTokenResponse(e);
                  }
                }}
                headerIdMessage={'paymentVerification'}
              />
            )}
          <PlaceOrderButton
            type="submit"
            form="paycometPaymentForm"
            confirmation={false}
            disabled={
              isProcessingPaymentPaycomet ||
              isPlacingOrder ||
              (!checkoutPaymentMethodId && !showAddPaymentMethod)
            }
            isLoading={isProcessingPaymentPaycomet || isPlacingOrder}
            margin={selectedMethod ? '1rem 0 0' : '0.5rem 0 0'}
            onClick={handleOnSubmit}
            placeOrderAriaLabel={placeOrderAriaLabel}
          />
        </PaymentSelection>
        <ErrorDialog />
      </PaymentSection>
    </StyledFullPageScreen>
  );
};

export default PaycometHostedPagePayment;
